import React from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LanguageSwitcherProps {
  className?: string;
  size?: "sm" | "md" | "lg";
}

// Displays EN / UA / RU as a compact segmented control and switches i18n language
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = "",
  size = "sm",
}) => {
  const { i18n } = useTranslation();
  const langs = [
    { label: "English", short: "EN", code: "en" },
    { label: "Українська", short: "UA", code: "uk" },
    { label: "Русский", short: "RU", code: "ru" },
  ];
  const active = (i18n.language || i18n.resolvedLanguage || "en").split("-")[0];

  const sizeClasses =
    size === "sm" ? "h-8 px-2" : size === "md" ? "h-9 px-3" : "h-11 w-11 p-0";

  const current = langs.find((l) => l.code === active) ?? langs[0];

  const selectLang = (code: string) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("i18nextLng", code);
    } catch (err) {
      // ignore persistence errors (e.g., private mode)
      void err;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-2 ${sizeClasses} ${className}`}
          aria-label="Language selector"
        >
          <Globe className="h-6 w-6 sm:h-7 sm:w-7" />
          <span className="hidden sm:inline">{current.short}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        <DropdownMenuLabel>Select language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {langs.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => selectLang(l.code)}
            className={l.code === active ? "font-medium" : ""}
          >
            <span className="mr-2 w-8 text-xs text-muted-foreground">
              {l.short}
            </span>
            <span>{l.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
